import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin);

// Styled Components
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 60vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  padding: 20px;
  position: relative;
  box-sizing: border-box;
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 12px;
  justify-content: center;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const monthNames = [
  { value: "ALL", label: "Cały rok" },
  { value: 1, label: "Styczeń" },
  { value: 2, label: "Luty" },
  { value: 3, label: "Marzec" },
  { value: 4, label: "Kwiecień" },
  { value: 5, label: "Maj" },
  { value: 6, label: "Czerwiec" },
  { value: 7, label: "Lipiec" },
  { value: 8, label: "Sierpień" },
  { value: 9, label: "Wrzesień" },
  { value: 10, label: "Październik" },
  { value: 11, label: "Listopad" },
  { value: 12, label: "Grudzień" },
];

// Colors (your palette)
const PURPLE = "#9848c7";                       // current
const PURPLE_BORDER = "rgba(152,72,199,1)";
const RED = "rgba(220,53,69,0.8)";              // previous
const RED_BORDER = "rgba(220,53,69,1)";
const LIGHT_PURPLE = "rgba(152,72,199,0.25)";

const HistogramPurchaseChart = ({ data }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("ALL");
  const [chartData, setChartData] = useState(null);
  const [groupedData, setGroupedData] = useState(null);
  const [avgCurr, setAvgCurr] = useState(0);
  const [avgPrev, setAvgPrev] = useState(0);

  const getDateParts = (dateString) => {
    const d = new Date(dateString);
    if (isNaN(d)) return { year: null, month: null };
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  };

  const calculateValue = (row) => {
    const v = parseFloat(row.K_42);
    return Number.isFinite(v) ? v : 0;
  };

  // Group once for speed
  useEffect(() => {
    if (!data || data.length === 0) return;

    const grouped = data.reduce((acc, row) => {
      if (!row.DataZakupu || !row.NazwaDostawcy) return acc;
      const { year, month } = getDateParts(row.DataZakupu);
      if (!year || !month) return acc;

      const supplier = row.NazwaDostawcy;
      const amount = calculateValue(row);

      if (!acc[year]) acc[year] = {};
      if (!acc[year][month]) acc[year][month] = {};
      if (!acc[year][month][supplier]) acc[year][month][supplier] = { total: 0, count: 0 };

      acc[year][month][supplier].total += amount;
      acc[year][month][supplier].count += 1;

      return acc;
    }, {});

    const years = Object.keys(grouped).map(Number).sort((a, b) => b - a);
    setAvailableYears(years);
    setSelectedYear((prev) => prev || years[0]);
    setSelectedMonth("ALL");
    setGroupedData(grouped);
  }, [data]);

  // Build chart with comparison to previous year
  useEffect(() => {
    if (!selectedYear || !groupedData) return;

    const yearDataCurr = groupedData[selectedYear] || {};
    const yearDataPrev = groupedData[selectedYear - 1] || {};

    // Aggregate supplier stats for a given year's data & month filter
    const buildSupplierMap = (yearData) => {
      if (!yearData) return {};
      if (selectedMonth === "ALL") {
        const acc = {};
        Object.values(yearData).forEach((monthData) => {
          Object.entries(monthData).forEach(([supplier, stats]) => {
            if (!acc[supplier]) acc[supplier] = { total: 0, count: 0 };
            acc[supplier].total += stats.total;
            acc[supplier].count += stats.count;
          });
        });
        return acc;
      }
      return yearData[selectedMonth] || {};
    };

    const mapCurr = buildSupplierMap(yearDataCurr);
    const mapPrev = buildSupplierMap(yearDataPrev);

    // Union of suppliers to ensure fair compare; rank by current-year average
    const suppliers = Array.from(new Set([...Object.keys(mapCurr), ...Object.keys(mapPrev)]));

    const toAvg = (entry) => (entry && entry.count > 0 ? entry.total / entry.count : 0);

    const rows = suppliers.map((name) => ({
      name,
      avgCurr: toAvg(mapCurr[name]),
      avgPrev: toAvg(mapPrev[name]),
    }));

    // Sort: primarily by current-year avg desc, fallback to prev-year avg
    rows.sort((a, b) => (b.avgCurr - a.avgCurr) || (b.avgPrev - a.avgPrev));

    const top = rows.slice(0, 10);

    const labels = top.map((s) => (s.name.length > 15 ? s.name.slice(0, 15) + "…" : s.name));
    const dataCurr = top.map((s) => s.avgCurr);
    const dataPrev = top.map((s) => s.avgPrev);

    // Global averages for annotation lines (over the displayed suppliers)
    const avgC = dataCurr.length ? dataCurr.reduce((a, b) => a + b, 0) / dataCurr.length : 0;
    const avgP = dataPrev.length ? dataPrev.reduce((a, b) => a + b, 0) / dataPrev.length : 0;
    setAvgCurr(avgC);
    setAvgPrev(avgP);

    setChartData({
      labels,
      datasets: [
        {
          label: `Średnia ${selectedYear}`,
          data: dataCurr,
          backgroundColor: "rgba(152,72,199,0.85)",
          borderColor: PURPLE_BORDER,
          borderWidth: 1,
        },
        {
          label: `Średnia ${selectedYear - 1}`,
          data: dataPrev,
          backgroundColor: RED,
          borderColor: RED_BORDER,
          borderWidth: 1,
        },
      ],
    });
  }, [selectedYear, selectedMonth, groupedData]);

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="zakupy-avg-info">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="zakupy-avg-info"
        place="left"
        content={
          <>
            <strong>Średnia wartość zakupu</strong>
            <br />
            Top 10 dostawców wg średniej (bieżący rok) z porównaniem do poprzedniego roku.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:&nbsp;</label>
          <select
            id="year-select"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="month-select">Miesiąc:&nbsp;</label>
          <select
            id="month-select"
            value={selectedMonth}
            onChange={(e) =>
              setSelectedMonth(e.target.value === "ALL" ? "ALL" : parseInt(e.target.value, 10))
            }
          >
            {monthNames.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      {chartData ? (
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: { display: true, text: "Średnia wartość zakupu (PLN)" },
                ticks: {
                  callback: (value) =>
                    Number(value).toLocaleString("pl-PL", { minimumFractionDigits: 0 }),
                },
              },
              x: {
                title: { display: true, text: "Dostawcy" },
              },
            },
            plugins: {
              legend: { position: "top" },
              title: {
                display: true,
                text: `Top 10 dostawców – Średnia wartość zakupu (${selectedYear} vs ${selectedYear - 1}${
                  selectedMonth === "ALL"
                    ? ", cały rok"
                    : `, ${monthNames.find((m) => m.value === selectedMonth)?.label}`
                })`,
              },
              tooltip: {
                callbacks: {
                  label: (ctx) =>
                    `${ctx.dataset.label}: ${ctx.raw.toLocaleString("pl-PL")} PLN`,
                },
              },
              annotation: {
                annotations: {
                  avgCurrLine: {
                    type: "line",
                    yMin: avgCurr,
                    yMax: avgCurr,
                    borderColor: PURPLE,
                    borderWidth: 2,
                    borderDash: [6, 6],
                    label: {
                      enabled: true,
                      content: `Śr. ${selectedYear}: ${avgCurr.toLocaleString("pl-PL")} PLN`,
                      backgroundColor: LIGHT_PURPLE,
                      color: PURPLE,
                      position: "end",
                    },
                  },
                  ...(avgPrev > 0
                    ? {
                        avgPrevLine: {
                          type: "line",
                          yMin: avgPrev,
                          yMax: avgPrev,
                          borderColor: RED_BORDER,
                          borderWidth: 2,
                          borderDash: [6, 6],
                          label: {
                            enabled: true,
                            content: `Śr. ${selectedYear - 1}: ${avgPrev.toLocaleString("pl-PL")} PLN`,
                            backgroundColor: "rgba(220,53,69,0.08)",
                            color: RED_BORDER,
                            position: "start",
                          },
                        },
                      }
                    : {}),
                },
              },
            },
          }}
        />
      ) : (
        <p style={{ textAlign: "center" }}>Brak danych dla wybranych filtrów.</p>
      )}
    </ChartWrapper>
  );
};

export default HistogramPurchaseChart;
